// landIeul が使っている recharts@2.15.4 (ResponsiveContainer + LineChart/BarChart + CartesianGrid +
// XAxis/YAxis + Legend + Line/Bar + Tooltip) と同じ DOM を素の TS で生成する汎用レンダラ。
//
// 実装は recharts / recharts-scale / d3-scale / d3-shape の該当ロジックの忠実移植:
//   - getNiceTickValues (recharts-scale)  … YAxis domain=[0,'auto'] の nice 化
//   - scaleLinear / scalePoint / scaleBand (d3-scale)
//   - curveMonotoneX / curveLinear (d3-shape) + pathRound(3) の数値丸め
//   - CartesianAxis.getTicks の preserveStartEnd 間引き (テキスト実測に基づく)
//   - CartesianGrid の水平/垂直グリッド座標 (getCoordinatesOfGrid)
//   - Legend の高さ実測 → offset.top への加算 (appendOffsetOfLegend)

export interface Margin {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface SeriesSpec {
  key: string;
  name: string;
  stroke: string;
  fill: string;
  strokeWidth: number;
  unit?: string;
}

export interface ChartSpec {
  kind: "line" | "bar";
  data: Array<Record<string, number | string | undefined>>;
  xKey: string;
  xPadding: { left: number; right: number };
  xTickFontSize: string;
  yTickFontSize: string;
  /** YAxis の ticks 明示指定 (無指定なら nice ticks) */
  yTicks?: number[];
  /** YAxis の domain 明示指定 (無指定なら [0, 'auto']) */
  yDomain?: [number, number];
  yUnit?: string;
  /** XAxis unit。目盛りラベルの後ろに付く */
  xUnit?: string;
  /** tickFormatter={(tick) => tick.toLocaleString()} 相当 */
  yLocaleString?: boolean;
  margin?: Partial<Margin>;
  curve?: "linear" | "monotone";
  legendWrapperStyle: Record<string, string>;
  tooltipFontSize?: string;
  /** recharts のアニメーション後に付く stroke-dasharray を再現するか */
  emitDashArray?: boolean;
  series: SeriesSpec[];
  /** ResponsiveContainer の style 文字列 */
  containerStyle: string;
}

const DEFAULT_MARGIN: Margin = { top: 5, right: 5, bottom: 5, left: 5 };
const Y_AXIS_WIDTH = 60;
const X_AXIS_HEIGHT = 30;
const TICK_SIZE = 6;
const TICK_MARGIN = 2;
const MIN_TICK_GAP = 5;
const TICK_COUNT = 5;

let clipIdSeq = 0;

/* ------------------------------------------------------------------ *
 * テキスト実測 (recharts util/DOMUtils#getStringSize 相当)
 * ------------------------------------------------------------------ */

const MEASURE_ID = "recharts_measurement_span";
const measureCache: Record<string, { width: number; height: number }> = {};

export function measureText(
  text: string,
  fontSize?: string,
): { width: number; height: number } {
  const key = `${fontSize || ""}|${text}`;
  const cached = measureCache[key];
  if (cached) return cached;
  let span = document.getElementById(MEASURE_ID) as HTMLSpanElement | null;
  if (!span) {
    span = document.createElement("span");
    span.setAttribute("id", MEASURE_ID);
    span.setAttribute("aria-hidden", "true");
    document.body.appendChild(span);
  }
  span.style.position = "absolute";
  span.style.top = "-20000px";
  span.style.left = "0";
  span.style.padding = "0";
  span.style.margin = "0";
  span.style.border = "none";
  span.style.whiteSpace = "pre";
  span.style.fontSize = fontSize || "";
  span.style.letterSpacing = fontSize ? "normal" : "";
  span.textContent = text;
  const rect = span.getBoundingClientRect();
  const result = { width: rect.width, height: rect.height };
  measureCache[key] = result;
  return result;
}

/* ------------------------------------------------------------------ *
 * recharts-scale: getNiceTickValues
 * ------------------------------------------------------------------ */

/** recharts-scale は Decimal.js で計算するため、浮動小数の誤差を丸めて等価にする */
function exact(value: number): number {
  if (!Number.isFinite(value) || value === 0) return value;
  return Number(value.toPrecision(15));
}

/** 浮動小数の誤差で ceil が 1 ずれるのを防ぐ */
function safeCeil(value: number): number {
  return Math.ceil(exact(value));
}

function getDigitCount(value: number): number {
  if (value === 0) return 1;
  return Math.floor(Math.log10(Math.abs(value))) + 1;
}

function getFormatStep(
  roughStep: number,
  allowDecimals: boolean,
  correctionFactor: number,
): number {
  if (roughStep <= 0) return 0;
  const digitCount = getDigitCount(roughStep);
  const digitCountValue = 10 ** digitCount;
  const stepRatio = roughStep / digitCountValue;
  const stepRatioScale = digitCount !== 1 ? 0.05 : 0.1;
  const amendStepRatio =
    (safeCeil(stepRatio / stepRatioScale) + correctionFactor) * stepRatioScale;
  const formatStep = exact(amendStepRatio * digitCountValue);
  return allowDecimals ? formatStep : Math.ceil(formatStep);
}

function calculateStep(
  min: number,
  max: number,
  tickCount: number,
  allowDecimals: boolean,
  correctionFactor = 0,
): { step: number; tickMin: number; tickMax: number } {
  if (!Number.isFinite((max - min) / (tickCount - 1))) {
    return { step: 0, tickMin: 0, tickMax: 0 };
  }
  const step = getFormatStep(
    (max - min) / (tickCount - 1),
    allowDecimals,
    correctionFactor,
  );
  let middle: number;
  if (min <= 0 && max >= 0) {
    middle = 0;
  } else {
    middle = (min + max) / 2;
    middle -= middle % step;
  }
  let belowCount = safeCeil((middle - min) / step);
  let aboveCount = safeCeil((max - middle) / step);
  const scaleCount = belowCount + aboveCount + 1;
  if (scaleCount > tickCount) {
    return calculateStep(min, max, tickCount, allowDecimals, correctionFactor + 1);
  }
  if (scaleCount < tickCount) {
    aboveCount = max > 0 ? aboveCount + (tickCount - scaleCount) : aboveCount;
    belowCount = max > 0 ? belowCount : belowCount + (tickCount - scaleCount);
  }
  return {
    step,
    tickMin: exact(middle - belowCount * step),
    tickMax: exact(middle + aboveCount * step),
  };
}

export function getNiceTickValues(
  domain: [number, number],
  tickCount = 6,
  allowDecimals = true,
): number[] {
  const count = Math.max(tickCount, 2);
  const cormin = Math.min(domain[0], domain[1]);
  const cormax = Math.max(domain[0], domain[1]);
  if (cormin === cormax) return [cormin];
  const { step, tickMin, tickMax } = calculateStep(
    cormin,
    cormax,
    count,
    allowDecimals,
  );
  if (step <= 0) return [cormin, cormax];
  const values: number[] = [];
  const limit = tickMax + 0.1 * step;
  for (let i = 0; ; i += 1) {
    const v = exact(tickMin + i * step);
    if (v >= limit) break;
    values.push(v);
  }
  return values;
}

/* ------------------------------------------------------------------ *
 * d3-scale 相当
 * ------------------------------------------------------------------ */

export function linearScale(
  domain: [number, number],
  range: [number, number],
): (x: number) => number {
  const [d0, d1] = domain;
  const [r0, r1] = range;
  return (x: number): number => {
    const t = d1 - d0 === 0 ? 0.5 : (x - d0) / (d1 - d0);
    return r0 * (1 - t) + r1 * t;
  };
}

/** d3-scale band/point の rescale() 相当 */
export function bandPositions(
  n: number,
  r0: number,
  r1: number,
  paddingInner: number,
): { start: number; step: number; bandwidth: number; values: number[] } {
  const step = (r1 - r0) / Math.max(1, n - paddingInner);
  const start = r0 + (r1 - r0 - step * (n - paddingInner)) * 0.5;
  const bandwidth = step * (1 - paddingInner);
  const values: number[] = [];
  for (let i = 0; i < n; i += 1) values.push(start + step * i);
  return { start, step, bandwidth, values };
}

/* ------------------------------------------------------------------ *
 * d3-shape 相当 (curveLinear / curveMonotoneX) + pathRound(3)
 * ------------------------------------------------------------------ */

function r3(value: number): string {
  return String(Math.round(value * 1000) / 1000);
}

interface Pt {
  x: number;
  y: number;
}

export function linearPath(points: Pt[]): string {
  return points
    .map((p, i) => `${i === 0 ? "M" : "L"}${r3(p.x)},${r3(p.y)}`)
    .join("");
}

function sign1(x: number): number {
  return x < 0 ? -1 : 1;
}

/** d3-shape MonotoneX */
export function monotonePath(points: Pt[]): string {
  let out = "";
  let x0 = NaN;
  let y0 = NaN;
  let x1 = NaN;
  let y1 = NaN;
  let t0 = NaN;
  let pointIndex = 0;

  const slope3 = (x2: number, y2: number): number => {
    const h0 = x1 - x0;
    const h1 = x2 - x1;
    const s0 = (y1 - y0) / (h0 || (h1 < 0 ? -0 : 0));
    const s1 = (y2 - y1) / (h1 || (h0 < 0 ? -0 : 0));
    const p = (s0 * h1 + s1 * h0) / (h0 + h1);
    return (
      (sign1(s0) + sign1(s1)) *
        Math.min(Math.abs(s0), Math.abs(s1), 0.5 * Math.abs(p)) || 0
    );
  };
  const slope2 = (t: number): number => {
    const h = x1 - x0;
    return h ? ((3 * (y1 - y0)) / h - t) / 2 : t;
  };
  const bezier = (ta: number, tb: number): void => {
    const dx = (x1 - x0) / 3;
    out += `C${r3(x0 + dx)},${r3(y0 + dx * ta)},${r3(x1 - dx)},${r3(
      y1 - dx * tb,
    )},${r3(x1)},${r3(y1)}`;
  };

  for (const pt of points) {
    const x = pt.x;
    const y = pt.y;
    if (x === x1 && y === y1) continue;
    let t1 = NaN;
    switch (pointIndex) {
      case 0:
        pointIndex = 1;
        out += `M${r3(x)},${r3(y)}`;
        break;
      case 1:
        pointIndex = 2;
        break;
      case 2:
        pointIndex = 3;
        t1 = slope3(x, y);
        bezier(slope2(t1), t1);
        break;
      default:
        t1 = slope3(x, y);
        bezier(t0, t1);
        break;
    }
    x0 = x1;
    x1 = x;
    y0 = y1;
    y1 = y;
    t0 = t1;
  }
  if (pointIndex === 2) out += `L${r3(x1)},${r3(y1)}`;
  else if (pointIndex === 3) bezier(t0, slope2(t0));
  return out;
}

/* ------------------------------------------------------------------ *
 * CartesianAxis.getTicks (interval="preserveStartEnd")
 * ------------------------------------------------------------------ */

interface TickItem {
  value: number | string;
  label: string;
  coordinate: number;
}

interface PlacedTick extends TickItem {
  tickCoord: number;
  isShow: boolean;
}

function isVisible(
  sign: number,
  tickPosition: number,
  size: number,
  start: number,
  end: number,
): boolean {
  if (sign * tickPosition < sign * start || sign * tickPosition > sign * end) {
    return false;
  }
  return (
    sign * (tickPosition - (sign * size) / 2 - start) >= 0 &&
    sign * (tickPosition + (sign * size) / 2 - end) <= 0
  );
}

function preserveStartEndTicks(
  ticks: TickItem[],
  sizeOf: (tick: TickItem) => number,
  boundaryStart: number,
  boundaryEnd: number,
): PlacedTick[] {
  const len = ticks.length;
  if (len === 0) return [];
  const sign =
    len >= 2 ? (ticks[1].coordinate - ticks[0].coordinate >= 0 ? 1 : -1) : 1;
  let start = boundaryStart;
  let end = boundaryEnd;
  const result = ticks.map((t) => ({ ...t, tickCoord: t.coordinate, isShow: false }));

  const tail = result[len - 1];
  const tailSize = sizeOf(tail);
  const tailGap = sign * (tail.coordinate + (sign * tailSize) / 2 - end);
  tail.tickCoord = tailGap > 0 ? tail.coordinate - tailGap * sign : tail.coordinate;
  if (isVisible(sign, tail.tickCoord, tailSize, start, end)) {
    end = tail.tickCoord - sign * (tailSize / 2 + MIN_TICK_GAP);
    tail.isShow = true;
  }

  for (let i = 0; i < len - 1; i += 1) {
    const entry = result[i];
    const size = sizeOf(entry);
    if (i === 0) {
      const gap = sign * (entry.coordinate - (sign * size) / 2 - start);
      entry.tickCoord = gap < 0 ? entry.coordinate - gap * sign : entry.coordinate;
    }
    if (isVisible(sign, entry.tickCoord, size, start, end)) {
      start = entry.tickCoord + sign * (size / 2 + MIN_TICK_GAP);
      entry.isShow = true;
    }
  }
  return result.filter((e) => e.isShow);
}

/* ------------------------------------------------------------------ *
 * HTML/SVG 組み立てヘルパ
 * ------------------------------------------------------------------ */

function esc(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function hexToRgb(hex: string): string {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!m) return hex;
  return `rgb(${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)})`;
}

function styleString(style: Record<string, string>): string {
  return Object.keys(style)
    .map((key) => `${key}: ${style[key]};`)
    .join(" ");
}

const LEGEND_LINE_ICON =
  "M0,16h10.666666666666666\n            A5.333333333333333,5.333333333333333,0,1,1,21.333333333333332,16\n            H32M21.333333333333332,16\n            A5.333333333333333,5.333333333333333,0,1,1,10.666666666666666,16";

function legendHtml(spec: ChartSpec): string {
  const items = spec.series
    .map((s, i) => {
      const icon =
        spec.kind === "bar"
          ? `<path stroke="none" fill="${s.fill}" d="M0,4h32v24h-32z" class="recharts-legend-icon"></path>`
          : `<path stroke-width="4" fill="none" stroke="${s.stroke}" d="${LEGEND_LINE_ICON}" class="recharts-legend-icon"></path>`;
      return `<li class="recharts-legend-item legend-item-${i}" style="display: inline-block; margin-right: 10px;"><svg class="recharts-surface" width="14" height="14" viewBox="0 0 32 32" style="display: inline-block; vertical-align: middle; margin-right: 4px;"><title></title><desc></desc>${icon}</svg><span class="recharts-legend-item-text" style="color: ${hexToRgb(
        spec.kind === "bar" ? s.fill : s.stroke,
      )};">${esc(s.name)}</span></li>`;
    })
    .join("");
  return `<ul class="recharts-default-legend" style="padding: 0px; margin: 0px; text-align: center;">${items}</ul>`;
}

/* ------------------------------------------------------------------ *
 * メイン
 * ------------------------------------------------------------------ */

export function renderChart(host: HTMLElement, spec: ChartSpec): void {
  host.innerHTML = "";
  const container = document.createElement("div");
  container.className = "recharts-responsive-container";
  container.setAttribute("style", spec.containerStyle);
  host.appendChild(container);

  const width = container.clientWidth;
  const height = container.clientHeight;
  if (!(width > 0) || !(height > 0)) return;

  const margin: Margin = { ...DEFAULT_MARGIN, ...(spec.margin || {}) };

  const wrapper = document.createElement("div");
  wrapper.className = "recharts-wrapper";
  wrapper.setAttribute(
    "style",
    `position: relative; cursor: default; width: 100%; height: 100%; max-height: ${height}px; max-width: ${width}px;`,
  );
  container.appendChild(wrapper);

  // Legend を先に置いて高さを実測する (recharts の appendOffsetOfLegend 相当)
  const legendWidth = width - margin.left - margin.right;
  const legend = document.createElement("div");
  legend.className = "recharts-legend-wrapper";
  legend.setAttribute(
    "style",
    styleString({
      position: "absolute",
      width: `${legendWidth}px`,
      height: "auto",
      left: `${margin.left}px`,
      top: `${margin.top}px`,
      ...spec.legendWrapperStyle,
    }),
  );
  // 系列が 0 件のとき recharts は Legend の中身を描かない (高さ 0 になる)
  legend.innerHTML = spec.series.length === 0 ? "" : legendHtml(spec);
  wrapper.appendChild(legend);
  const legendHeight = legend.getBoundingClientRect().height;

  const offset = {
    left: margin.left + Y_AXIS_WIDTH,
    top: margin.top + legendHeight,
    width: width - margin.left - Y_AXIS_WIDTH - margin.right,
    height: height - margin.top - legendHeight - X_AXIS_HEIGHT - margin.bottom,
  };

  const svg = buildSvg(spec, width, height, margin, offset);
  wrapper.insertAdjacentHTML("afterbegin", svg);

  const tooltipStyle: Record<string, string> = {
    visibility: "hidden",
    "pointer-events": "none",
    position: "absolute",
    top: "0px",
    left: "0px",
  };
  if (spec.tooltipFontSize) tooltipStyle["font-size"] = spec.tooltipFontSize;
  tooltipStyle.transform = `translate(${offset.left}px, ${offset.top}px)`;
  wrapper.insertAdjacentHTML(
    "beforeend",
    `<div tabindex="-1" class="recharts-tooltip-wrapper recharts-tooltip-wrapper-right recharts-tooltip-wrapper-bottom" style="${styleString(
      tooltipStyle,
    )}"><div class="recharts-default-tooltip" style="margin: 0px; padding: 10px; background-color: rgb(255, 255, 255); border: 1px solid rgb(204, 204, 204); white-space: nowrap;"><p class="recharts-tooltip-label" style="margin: 0px;"></p></div></div>`,
  );

  if (spec.emitDashArray) {
    wrapper.querySelectorAll<SVGPathElement>(".recharts-line-curve").forEach((path) => {
      const total = path.getTotalLength();
      path.setAttribute("stroke-dasharray", `${total}px 0px`);
    });
  }
}

interface Offset {
  left: number;
  top: number;
  width: number;
  height: number;
}

function yTickLabel(spec: ChartSpec, value: number): string {
  const base = spec.yLocaleString ? value.toLocaleString() : String(value);
  return `${base}${spec.yUnit || ""}`;
}

function buildSvg(
  spec: ChartSpec,
  width: number,
  height: number,
  margin: Margin,
  offset: Offset,
): string {
  clipIdSeq += 1;
  const clipId = `recharts${clipIdSeq}-clip`;
  const data = spec.data;
  const isBar = spec.kind === "bar";

  // データ 0 件のとき recharts は目盛りも系列も出さず、CartesianGrid の外枠 4 本だけを描く
  // (ローカル DB に e-Stat データが無いページで実際にこの状態になる)。
  if (data.length === 0) return emptySvg(clipId, width, height, offset);

  // ---- X 軸 (category / point or band scale) ----
  const xRange: [number, number] = [
    offset.left + spec.xPadding.left,
    offset.left + offset.width - spec.xPadding.right,
  ];
  const band = bandPositions(data.length, xRange[0], xRange[1], isBar ? 0 : 1);
  const bandOffset = isBar ? band.bandwidth / 2 : 0;
  const xTicksAll: TickItem[] = data.map((row, i) => ({
    value: row[spec.xKey] as number | string,
    label: `${row[spec.xKey]}${spec.xUnit || ""}`,
    coordinate: band.values[i] + bandOffset,
  }));

  // ---- Y 軸 (number scale) ----
  const values: number[] = [];
  data.forEach((row) => {
    spec.series.forEach((s) => {
      const v = row[s.key];
      if (typeof v === "number" && Number.isFinite(v)) values.push(v);
    });
  });
  const dataMin = values.length ? Math.min(...values) : 0;
  const dataMax = values.length ? Math.max(...values) : 0;

  let yDomain: [number, number];
  let niceTicks: number[] | null = null;
  if (spec.yDomain) {
    yDomain = [
      Math.min(spec.yDomain[0], dataMin),
      Math.max(spec.yDomain[1], dataMax),
    ];
  } else {
    niceTicks = getNiceTickValues([Math.min(0, dataMin), dataMax], TICK_COUNT, true);
    yDomain = [niceTicks[0], niceTicks[niceTicks.length - 1]];
  }
  const yScale = linearScale(yDomain, [
    offset.top + offset.height,
    offset.top,
  ]);
  const yValues = spec.yTicks || niceTicks || [yDomain[0], yDomain[1]];
  const yTicksAll: TickItem[] = yValues.map((v) => ({
    value: v,
    label: yTickLabel(spec, v),
    coordinate: yScale(v),
  }));

  // ---- 目盛りの間引き ----
  const xSize = (t: TickItem): number => measureText(t.label, spec.xTickFontSize).width;
  const ySize = (t: TickItem): number => measureText(t.label, spec.yTickFontSize).height;
  // CartesianGrid は fontSize 未指定でテキスト実測する (recharts の実挙動)
  const yGridSize = (t: TickItem): number => measureText(t.label).height;

  const xTicks = preserveStartEndTicks(xTicksAll, xSize, 0, width);
  const yTicks = preserveStartEndTicks(yTicksAll, ySize, height, 0);
  const xGridTicks = preserveStartEndTicks(xTicksAll, xSize, 0, width);
  const yGridTicks = preserveStartEndTicks(yTicksAll, yGridSize, height, 0);

  // ---- CartesianGrid ----
  const horizontal = coordinatesOfGrid(
    yGridTicks.map((t) => t.coordinate),
    offset.top,
    offset.top + offset.height,
  );
  const vertical = coordinatesOfGrid(
    xGridTicks.map((t) => t.coordinate),
    offset.left,
    offset.left + offset.width,
  );
  const gridAttrs = `stroke-dasharray="3 3" stroke="#ccc" fill="none" x="${offset.left}" y="${offset.top}" width="${offset.width}" height="${offset.height}"`;
  const gridSvg =
    `<g class="recharts-cartesian-grid"><g class="recharts-cartesian-grid-horizontal">` +
    horizontal
      .map(
        (y) =>
          `<line ${gridAttrs} x1="${offset.left}" y1="${y}" x2="${
            offset.left + offset.width
          }" y2="${y}"></line>`,
      )
      .join("") +
    `</g><g class="recharts-cartesian-grid-vertical">` +
    vertical
      .map(
        (x) =>
          `<line ${gridAttrs} x1="${x}" y1="${offset.top}" x2="${x}" y2="${
            offset.top + offset.height
          }"></line>`,
      )
      .join("") +
    `</g></g>`;

  // ---- XAxis ----
  const xAxisY = offset.top + offset.height;
  const xAxisBase = `orientation="bottom" width="${offset.width}" height="${X_AXIS_HEIGHT}" x="${offset.left}" y="${xAxisY}"`;
  // NOTE: <text> には x/y をベースに含めない。属性重複時はブラウザが先勝ちで解釈するため、
  // ベースの x/y が tick 座標の x/y を潰してラベルが軸原点に張り付く (recharts も text には width/height のみ)。
  const xAxisTextBase = `orientation="bottom" width="${offset.width}" height="${X_AXIS_HEIGHT}"`;
  const xAxisSvg =
    `<g class="recharts-layer recharts-cartesian-axis recharts-xAxis xAxis">` +
    `<line ${xAxisBase} class="recharts-cartesian-axis-line" stroke="#666" fill="none" x1="${offset.left}" y1="${xAxisY}" x2="${
      offset.left + offset.width
    }" y2="${xAxisY}"></line>` +
    `<g class="recharts-cartesian-axis-ticks">` +
    xTicks
      .map(
        (t) =>
          `<g class="recharts-layer recharts-cartesian-axis-tick">` +
          `<line ${xAxisBase} class="recharts-cartesian-axis-tick-line" stroke="#666" fill="none" x1="${t.coordinate}" y1="${
            xAxisY + TICK_SIZE
          }" x2="${t.coordinate}" y2="${xAxisY}"></line>` +
          `<text ${xAxisTextBase} stroke="none" font-size="${spec.xTickFontSize}" x="${t.tickCoord}" y="${
            xAxisY + TICK_SIZE + TICK_MARGIN
          }" class="recharts-text recharts-cartesian-axis-tick-value" text-anchor="middle" fill="#666">` +
          `<tspan x="${t.tickCoord}" dy="0.71em">${esc(t.label)}</tspan></text></g>`,
      )
      .join("") +
    `</g></g>`;

  // ---- YAxis ----
  const yAxisBase = `orientation="left" width="${Y_AXIS_WIDTH}" height="${offset.height}" x="${margin.left}" y="${offset.top}"`;
  // NOTE: XAxis と同じく <text> には x/y を含めない (属性重複の先勝ちでラベルが offset.top に堆積するのを防ぐ)
  const yAxisTextBase = `orientation="left" width="${Y_AXIS_WIDTH}" height="${offset.height}"`;
  const yAxisSvg =
    `<g class="recharts-layer recharts-cartesian-axis recharts-yAxis yAxis">` +
    `<line ${yAxisBase} class="recharts-cartesian-axis-line" stroke="#666" fill="none" x1="${offset.left}" y1="${offset.top}" x2="${offset.left}" y2="${
      offset.top + offset.height
    }"></line>` +
    `<g class="recharts-cartesian-axis-ticks">` +
    yTicks
      .map(
        (t) =>
          `<g class="recharts-layer recharts-cartesian-axis-tick">` +
          `<line ${yAxisBase} class="recharts-cartesian-axis-tick-line" stroke="#666" fill="none" x1="${
            offset.left - TICK_SIZE
          }" y1="${t.coordinate}" x2="${offset.left}" y2="${t.coordinate}"></line>` +
          `<text ${yAxisTextBase} stroke="none" font-size="${spec.yTickFontSize}" x="${
            offset.left - TICK_SIZE - TICK_MARGIN
          }" y="${t.tickCoord}" class="recharts-text recharts-cartesian-axis-tick-value" text-anchor="end" fill="#666">` +
          `<tspan x="${offset.left - TICK_SIZE - TICK_MARGIN}" dy="0.355em">${esc(
            t.label,
          )}</tspan></text></g>`,
      )
      .join("") +
    `</g></g>`;

  // ---- 系列 ----
  const seriesSvg = isBar
    ? barSeriesSvg(spec, data, band, offset, yScale)
    : lineSeriesSvg(spec, data, xTicksAll, offset, yScale);

  return (
    `<svg class="recharts-surface" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" style="width: 100%; height: 100%;">` +
    `<title></title><desc></desc>` +
    `<defs><clipPath id="${clipId}"><rect x="${offset.left}" y="${offset.top}" height="${offset.height}" width="${offset.width}"></rect></clipPath></defs>` +
    gridSvg +
    xAxisSvg +
    yAxisSvg +
    seriesSvg +
    `</svg>`
  );
}

/** データ 0 件時の SVG (グリッドの外枠のみ)。coordinatesOfGrid([], min, max) と同じ 2 本ずつになる。 */
function emptySvg(
  clipId: string,
  width: number,
  height: number,
  offset: Offset,
): string {
  const right = offset.left + offset.width;
  const bottom = offset.top + offset.height;
  const gridAttrs = `stroke-dasharray="3 3" stroke="#ccc" fill="none" x="${offset.left}" y="${offset.top}" width="${offset.width}" height="${offset.height}"`;
  const line = (x1: number, y1: number, x2: number, y2: number): string =>
    `<line ${gridAttrs} x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}"></line>`;
  return (
    `<svg class="recharts-surface" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" style="width: 100%; height: 100%;">` +
    `<title></title><desc></desc>` +
    `<defs><clipPath id="${clipId}"><rect x="${offset.left}" y="${offset.top}" height="${offset.height}" width="${offset.width}"></rect></clipPath></defs>` +
    `<g class="recharts-cartesian-grid"><g class="recharts-cartesian-grid-horizontal">` +
    line(offset.left, offset.top, right, offset.top) +
    line(offset.left, bottom, right, bottom) +
    `</g><g class="recharts-cartesian-grid-vertical">` +
    line(offset.left, offset.top, offset.left, bottom) +
    line(right, offset.top, right, bottom) +
    `</g></g></svg>`
  );
}

function coordinatesOfGrid(
  coords: number[],
  minValue: number,
  maxValue: number,
): number[] {
  const values = coords.slice();
  const hasMin = coords.some((c) => c === minValue);
  const hasMax = coords.some((c) => c === maxValue);
  if (!hasMin) values.push(minValue);
  if (!hasMax) values.push(maxValue);
  return values;
}

function lineSeriesSvg(
  spec: ChartSpec,
  data: ChartSpec["data"],
  xTicks: TickItem[],
  offset: Offset,
  yScale: (v: number) => number,
): string {
  return spec.series
    .map((s) => {
      const points: Pt[] = [];
      data.forEach((row, i) => {
        const v = row[s.key];
        if (typeof v !== "number" || !Number.isFinite(v)) return;
        points.push({ x: xTicks[i].coordinate, y: yScale(v) });
      });
      if (points.length === 0) return "";
      const d =
        spec.curve === "monotone" ? monotonePath(points) : linearPath(points);
      const common = `width="${offset.width}" height="${offset.height}"`;
      const dots = points
        .map(
          (p) =>
            `<circle r="3" name="${esc(s.name)}" fill="${s.fill}" stroke="${
              s.stroke
            }" stroke-width="${s.strokeWidth}" ${common} cx="${p.x}" cy="${
              p.y
            }" class="recharts-dot recharts-line-dot"></circle>`,
        )
        .join("");
      return (
        `<g class="recharts-layer recharts-line">` +
        `<path name="${esc(s.name)}" fill="none" stroke="${s.stroke}" stroke-width="${
          s.strokeWidth
        }" ${common} class="recharts-curve recharts-line-curve" d="${d}"></path>` +
        `<g class="recharts-layer"></g>` +
        `<g class="recharts-layer recharts-line-dots">${dots}</g>` +
        `</g>`
      );
    })
    .join("");
}

function barSeriesSvg(
  spec: ChartSpec,
  data: ChartSpec["data"],
  band: { start: number; step: number; bandwidth: number; values: number[] },
  offset: Offset,
  yScale: (v: number) => number,
): string {
  // recharts getBarPosition (barGap=4 / barCategoryGap='10%')
  const len = spec.series.length;
  const bandSize = band.bandwidth;
  const categoryOffset = bandSize * 0.1;
  let realBarGap = 4;
  if (bandSize - 2 * categoryOffset - (len - 1) * realBarGap <= 0) realBarGap = 0;
  let barSize = (bandSize - 2 * categoryOffset - (len - 1) * realBarGap) / len;
  if (barSize > 1) barSize = Math.floor(barSize);
  const baseY = yScale(0);

  return spec.series
    .map((s, si) => {
      const posOffset = categoryOffset + (realBarGap + barSize) * si;
      const rects = data
        .map((row, i) => {
          const v = row[s.key];
          if (typeof v !== "number" || !Number.isFinite(v)) return "";
          const x = band.values[i] + posOffset;
          const yTop = yScale(v);
          const y = Math.min(yTop, baseY);
          const h = Math.abs(baseY - yTop);
          return (
            `<g class="recharts-layer recharts-bar-rectangle">` +
            `<path x="${x}" y="${y}" width="${barSize}" height="${h}" radius="0" name="${esc(
              s.name,
            )}" stroke="${s.stroke}" fill="${s.fill}" stroke-width="${
              s.strokeWidth
            }" class="recharts-rectangle" d="M ${x},${y} h ${barSize} v ${h} h ${-barSize} Z"></path>` +
            `</g>`
          );
        })
        .join("");
      return (
        `<g class="recharts-layer recharts-bar">` +
        `<g class="recharts-layer recharts-bar-rectangles"><g class="recharts-layer">${rects}</g></g>` +
        `<g class="recharts-layer"></g>` +
        `</g>`
      );
    })
    .join("");
}

/** window.matchMedia('(max-width: 767px)') — landIeul の useIsSp 相当 */
export function isSp(): boolean {
  return window.matchMedia("(max-width: 767px)").matches;
}

/** ResponsiveContainer のリサイズ追従用 (連続する resize をまとめる) */
export function debounce(callback: () => void, wait = 100): () => void {
  let timer: number | undefined;
  return (): void => {
    if (timer) window.clearTimeout(timer);
    timer = window.setTimeout(callback, wait);
  };
}
